import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useLocalStorage } from './useLocalStorage';
import { localStorageActions, localStorageSlice, LocalStorageState } from '../store';

export type UseLocalStorageReduxSetValue<T> = T | ((previousValue: T) => T);
export type UseLocalStorageReduxResult<T> = [value: T, setValue: (newValue: UseLocalStorageReduxSetValue<T>) => void];

export function useLocalStorageRedux<T>(key: string): UseLocalStorageReduxResult<T | undefined>;

export function useLocalStorageRedux<T>(key: string, initialValue: T): UseLocalStorageReduxResult<T>;

export function useLocalStorageRedux<T>(key: string, initialValue?: T): UseLocalStorageReduxResult<T | undefined> {
  const { getValue, setValue } = useLocalStorage<T>(key);

  const dispatch = useDispatch();
  const value = useSelector<Record<string, LocalStorageState | undefined>, T | undefined>(
    (state) => state[localStorageSlice.name]?.[key] as T | undefined
  );
  const state = typeof value === 'undefined' ? initialValue : value;
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const setState = useCallback(
    (newValue: UseLocalStorageReduxSetValue<T | undefined>) => {
      const nextValue =
        typeof newValue === 'function'
          ? (newValue as (previousValue: T | undefined) => T | undefined)(stateRef.current)
          : newValue;

      stateRef.current = nextValue;
      dispatch(localStorageActions.setValue({ key, value: nextValue }));
    },
    [dispatch, key]
  );

  const isLoaded = useRef(false);

  useEffect(() => {
    if (!isLoaded.current) {
      // load from localStorage or set to the initialValue on the first update
      const value = getValue();
      setState(value || initialValue);
      isLoaded.current = true;
      return;
    }

    setValue(state);
  }, [getValue, initialValue, setState, setValue, state]);

  return [state, setState];
}

export default useLocalStorageRedux;
